import React, { useEffect, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, Form } from 'formik';
import { getRoute, loading, updateSession } from '../../../actions/sessions';
import { asyncActions, clearErrors, handleAsyncError } from '../../../actions/general';
import { validatePhone } from '../../../actions/users';
import ApplicationRecord from '../../../records/application';
import UserRecord from '../../../records/user';
import { daysAsMonths } from '../../../lib/formatters';

import {
  subDomains,
  formStyles,
  suffixes
} from '../../../constants/SiteVariables';

import useRouteStatus from '../../../hooks/useRouteStatus';
import useValidateRoute from '../../../hooks/useValidateRoute';
import useSessionEntity from '../../../hooks/useSessionEntity';
import Loading from '../../shared/Loading';
import Error from '../../shared/Error';
import ColumnInput from '../../shared/ColumnInput';
import StickyInput from '../../shared/StickyInput';
import StickySelect from '../../shared/StickySelect';
import Heap from '../../../lib/heap';
import GA from '../../../lib/ga';

import { setHeight } from '../../../lib/modal';

const heap = new Heap('NewUser');
const ga = new GA();

export default function NewUser({location}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector(state => state.sessions.get('loading'));
  const session = useSelector(state => state.sessions.get('session'));
  const pi4Confirmed = useSelector(state => state.sessions.get('pi4_confirmed'))
  const application = useSessionEntity('application');
  const shopping_cart = useSessionEntity('shopping_cart');
  const merchant = useSessionEntity('merchant')
  const user = useSessionEntity('user');
  const prequalifiedUser = useSelector(state => state.entities.get('prequalification').first());
  const asyncErrors = useSelector(state => state.general.get('errors'));
  const [newUser, setNewUser] = useState(user);
  const [foundUser, setFoundUser] = useState(false);

  useRouteStatus(location);

  useEffect(() => {
    dispatch(loading(false));
  }, []);

  useEffect(() => {
    if (!user && !isLoading) {
      const u = new UserRecord(shopping_cart && shopping_cart.billing_address);
      setNewUser(u.set('phone_number', ''));
    }
  }, [isLoading]);

  useEffect(() => {
    if (merchant && merchant.isOnHold) history.push('/error');

    if (newUser && merchant) {
      const content = document.getElementById('content');
      (content && session.mode === 'modal') && setHeight(content.clientHeight + 220);
    }
  }, [newUser, merchant]);

  useEffect(() => {
    if (foundUser) history.push("/init/existing_user");
  }, [foundUser]);

  useEffect(() => {
    if (asyncErrors.count() > 0 && asyncErrors.find(e => e && e.includes('User already registered'))) {
      setFoundUser(true);
      dispatch(loading(false));
    }
  }, [asyncErrors]);

  const checkPhone = async (val) => {
    const res = await validatePhone(val)();
    return res.result;
  }

  const save = values => {
    dispatch(loading(true));

    ga.identify({
      'id': newUser.id,
      'sessionId': session.id,
      'sessionKey': session.session_key,
      'merchantId': merchant.id,
      'publicKey': session.public_key,
    });

    heap.track({merchant: merchant, session, user: newUser, payload: { amount: shopping_cart ? shopping_cart.grand_total : 0 } });

    // set phone for business to be same as initial primary mobile reported by user
    values.business_phone = values.phone_number;

    return dispatch(asyncActions(
      updateSession({
        ...session.setCustomFlag(subDomains.subDomain()).toJS(),
        user: new UserRecord({ ...values }),
        application: new ApplicationRecord({ ...values, session_id: session.id }).toJS()
      }, { cart: false, prequal: false, merchant: false }),
      getRoute()
    ))
    .then(res => dispatch(loading(false)));
  }

  const isRecharge = session && session.id && session.hasCustomFlag('recharge');

  const StandardHeader = () => {
    return (
      <>
        <div className="is-size-4 has-text-weight-bold" style={{ lineHeight: '2rem' }}>Easy monthly terms for business purchases</div>

        {merchant.active_promo.get('max_net_payment') > 30 ?
          <><div className="mt-3 text-green-highlight is-size-2">No payments for {merchant.active_promo.get('max_net_payment')} days</div> <strong>No early pay-off fees.</strong></> :
          <div className="mt-3 text-green-highlight is-size-2">0% for {daysAsMonths(merchant.active_promo.get('max_net_duration'))}</div>}
        <div className="my-3">Enter a few details and get a decision in seconds.</div>
      </>
    );
  };

  const RechargeHeader = () => {
    return (
      <div className="is-size-4 has-text-weight-bold my-3" style={{ lineHeight: '2rem' }}>Let's start with the basics</div>
    );
  };

  if (!useValidateRoute({ company: true })) return (<></>);

  if ((newUser && newUser.account_id) && !pi4Confirmed) return <Redirect to="/init/conversion" />

  if (!newUser || !merchant) {
    return (<Loading active={true} />);
  }

  if (newUser && merchant) {
    return (
      <div role="main" id="content" className="has-text-centered">
        {prequalifiedUser && <div role="prequalification" className="title">Welcome, {prequalifiedUser.owner_first_name}<br/>We look forward to working with you.</div>}

        {isRecharge ? RechargeHeader() : StandardHeader()}

        <div className="has-text-weight-bold">This is a business {isRecharge ? 'application' : 'loan'}.</div>
        <div className="mb-5 has-text-weight-bold">Applying will <u>not</u> affect your personal credit.</div>

        <Error />

        {!isRecharge && <div className="px-4 py-4 mb-5 box is-shadowless new-user-box">
          <div className="mb-3 has-text-weight-semibold"><u>Requirements:</u></div>

          <ul className="has-text-left">
            <li className="mb-3">
              <FontAwesomeIcon icon={['fal', 'check-circle']} className="text-green-highlight ml-4" />
              <span className="ml-4">600+ FICO</span>
            </li>
            <li className="mb-3">
              <FontAwesomeIcon icon={['fal', 'check-circle']} className="text-green-highlight ml-4" />
              <span className="ml-4">A valid, operating business</span>
            </li>
            <li>
              <FontAwesomeIcon icon={['fal', 'check-circle']} className="text-green-highlight ml-4" />
              <span className="ml-4">$40k+ gross annual revenue</span>
            </li>
          </ul>
        </div>}

        <Formik
          initialValues={{ ...newUser.toJS(), dba: (application && application.dba) || ''}}
          enableReinitialize={true}
          validate={values => {
            const errors = {};
            if (!values.first_name) errors.first_name = '* Required';
            if (!values.last_name) errors.last_name = '* Required';

            if (!values.phone_number) {
              errors.phone_number = '* Required';
            } else if (values.phone_number.replace(/[^\d]/g, '').length < 10) {
              errors.phone_number = '* Please enter a valid phone number';
            }

            if (!values.company_name) {
              errors.company_name = '* Required';
            } else if (values.company_name.trim().length < 2) {
              errors.company_name = '* Company Name must be at least 2 characters';
            }

            if (!values.email) {
              errors.email = '* Required'
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
              errors.email = '* Invalid email address';
            }

            return errors;
          }}
          onSubmit={async (values, { setFieldError }) => {
            dispatch(loading(true));

            try {
              const check = await checkPhone(values.phone_number);

              if (!check.valid) {
                setFieldError('phone_number', '* Please enter a valid phone number');
                dispatch(loading(false));
              } else {
                save(values);
              }
            } catch (e) {
              dispatch(handleAsyncError(e));
            }
          }}
        >
          {({ isSubmitting, errors }) => (
            <Form>

              <ColumnInput error={errors.first_name}>
                <StickyInput
                  id="first_name"
                  placeholder="First Name"
                />
              </ColumnInput>

              <ColumnInput error={errors.last_name}>
                <StickyInput
                  className="is-three-quarters"
                  id="last_name"
                  placeholder="Last Name"
                />

                <StickySelect
                  error={errors.suffix}
                  id="suffix"
                  placeholder="Suffix"
                  options={suffixes}
                />
              </ColumnInput>

              <ColumnInput error={errors.email}>
                <StickyInput
                  id="email"
                  placeholder="Owner Email"
                />
              </ColumnInput>

              <ColumnInput error={errors.phone_number}>
                <StickyInput
                  id="phone_number"
                  mask="(999) 999-9999"
                  placeholder="Mobile Phone"
                />
              </ColumnInput>

              <ColumnInput error={errors.company_name}>
                <StickyInput
                  id="company_name"
                  placeholder="Company Name"
                />
              </ColumnInput>

              <ColumnInput>
                <StickyInput
                  id="dba"
                  placeholder="DBA, if any"
                />
              </ColumnInput>

              <div className="small-text mb-4 px-3">
                By creating an account, I agree to the <a href="https://www.creditkey.com/legal/terms-of-service-and-electronic-disclosures" target="_new">Credit Key Terms of Service &amp; Electronic Disclosures</a> and <a href="https://www.creditkey.com/legal/privacy-policy" target="_new">Privacy Policy</a>
              </div>

              <button
                type="submit"
                role="submit"
                disabled={isLoading}
                className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
                  'is-loading': isLoading
                })}>
                Continue
              </button>
            </Form>
          )}
        </Formik>

        <div className="mt-3 mb-3 has-text-weight-bold">
          <Link to="/init/existing_user">Already have an account?</Link>
        </div>
      </div>
    );
  }
}
